import type { Map as MapboxMap, SymbolLayerSpecification } from "mapbox-gl";

export const MAP_PIN_IMAGE_URL = "/marker.svg";
export const MAP_PIN_WIDTH = 40;
// Le tracé du marqueur s'arrête au-dessus du bas de son image : sans ce décalage, la pointe
// flotterait au-dessus de la coordonnée sur laquelle l'icône est ancrée.
export const MAP_PIN_TIP_OFFSET: [number, number] = [0, 2];

export interface MapPinImage {
  id: string;
  url: string;
  width: number;
}

export function mapPinSymbolLayout(imageId: string): NonNullable<SymbolLayerSpecification["layout"]> {
  return {
    "icon-image": imageId,
    "icon-anchor": "bottom",
    "icon-offset": MAP_PIN_TIP_OFFSET,
    "icon-size": 1,
    "icon-allow-overlap": true,
    "icon-ignore-placement": true,
  };
}

const rasterizations = new Map<string, Promise<{ data: ImageData; pixelRatio: number }>>();

function rasterizePin(url: string, width: number): Promise<{ data: ImageData; pixelRatio: number }> {
  const cacheKey = `${url}|${width}`;
  const cached = rasterizations.get(cacheKey);
  if (cached) return cached;

  const rasterization = new Promise<{ data: ImageData; pixelRatio: number }>((resolve, reject) => {
    const image = new Image();
    image.decoding = "async";
    image.onload = () => {
      const pixelRatio = Math.max(2, Math.ceil(window.devicePixelRatio || 1));
      const aspect = image.naturalHeight / image.naturalWidth || 1;
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(width * pixelRatio);
      canvas.height = Math.round(width * aspect * pixelRatio);
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("2d context unavailable"));
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      resolve({ data: ctx.getImageData(0, 0, canvas.width, canvas.height), pixelRatio });
    };
    image.onerror = reject;
    image.src = url;
  });
  rasterizations.set(cacheKey, rasterization);
  return rasterization;
}

async function ensureMapPinImage(m: MapboxMap, pin: MapPinImage) {
  if (m.hasImage(pin.id)) return;
  const { data, pixelRatio } = await rasterizePin(pin.url, pin.width);
  if (!m.hasImage(pin.id)) m.addImage(pin.id, data, { pixelRatio });
}

export function bindMapPinImage(m: MapboxMap, pin: MapPinImage) {
  void ensureMapPinImage(m, pin);
  const onImageMissing = (e: { id: string }) => {
    if (e.id === pin.id) void ensureMapPinImage(m, pin);
  };
  m.on("styleimagemissing", onImageMissing);
  return () => {
    m.off("styleimagemissing", onImageMissing);
  };
}
